import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useNavigate } from 'react-router-dom';
import type { ImportColumns, ImportField, ImportPreview, ImportResult } from '../../../types/import';
import { NO_COLUMN, useExcelImport } from './useExcelImport';

interface ExcelImportPageProps {
  title: string;
  description: string;
  companyId: number | null;
  backPath: string;
  backLabel: string;
  fields: ImportField[];
  loadPreview: (file: File) => Promise<ImportPreview>;
  runImport: (companyId: number, file: File, columns: ImportColumns) => Promise<ImportResult>;
}

function columnLabel(header: string, index: number): string {
  return header || `Колонка ${index + 1}`;
}

export default function ExcelImportPage({
  title,
  description,
  companyId,
  backPath,
  backLabel,
  fields,
  loadPreview,
  runImport,
}: ExcelImportPageProps) {
  const navigate = useNavigate();

  const { data, mapping, actions } = useExcelImport({
    companyId,
    fields,
    loadPreview,
    runImport,
  });

  const handleBack = () => {
    navigate(backPath);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    e.target.value = '';
    if (selected) {
      actions.handleFileSelected(selected);
    }
  };

  const isMappedColumn = (index: number): boolean => (
    Object.values(mapping.selectedColumns).includes(index.toString())
  );

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 10,
          backgroundColor: 'background.default',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          py: 1,
          mb: 2,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          size="small"
        >
          {backLabel}
        </Button>
        <Typography
          variant="button"
          component="h1"
          noWrap
          sx={{
            minWidth: 0,
            pr: { xs: '9px', sm: '10px' },
            color: 'primary.main',
          }}
        >
          {title}
        </Typography>
      </Box>

      {/* Content */}
      <Box sx={{ maxWidth: 900, mx: 'auto' }}>
        {data.error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
          >
            {data.error}
          </Alert>
        )}

        {data.result ? (
          <Paper sx={{ p: 2 }}>
            <Alert severity={data.result.errors.length > 0 ? 'warning' : 'success'}>
              Створено: {data.result.created}, пропущено дублікатів: {data.result.skipped}, помилок: {data.result.errors.length}
            </Alert>
            {data.result.errors.map((rowError) => (
              <Typography
                key={rowError.row}
                variant="body2"
                color="error"
                sx={{ mt: 1 }}
              >
                Рядок {rowError.row}: {rowError.message}
              </Typography>
            ))}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1,
                mt: 3,
              }}
            >
              <Button
                color="inherit"
                onClick={actions.handleImportAnother}
              >
                Імпортувати ще
              </Button>
              <Button
                variant="contained"
                onClick={handleBack}
              >
                {backLabel}
              </Button>
            </Box>
          </Paper>
        ) : (
          <Paper sx={{ p: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              {description}
            </Typography>

            <Button
              component="label"
              color="inherit"
              startIcon={<UploadFileIcon />}
              disabled={data.loading}
            >
              {data.file ? data.file.name : 'Обрати файл'}
              <input
                hidden
                type="file"
                accept=".xlsx"
                onChange={handleFileChange}
              />
            </Button>

            {data.preview && (
              <Box sx={{ mt: 3 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 2,
                    mb: 2,
                  }}
                >
                  {fields.map((field) => (
                    <TextField
                      key={field.key}
                      select
                      fullWidth
                      size="small"
                      label={field.required ? `${field.label} *` : field.label}
                      value={mapping.selectedColumns[field.key] ?? NO_COLUMN}
                      onChange={(e) => mapping.setColumn(field.key, e.target.value)}
                    >
                      {!field.required && <MenuItem value={NO_COLUMN}>— немає —</MenuItem>}
                      {data.preview?.headers.map((header, index) => (
                        <MenuItem
                          key={index}
                          value={index.toString()}
                        >
                          {columnLabel(header, index)}
                        </MenuItem>
                      ))}
                    </TextField>
                  ))}
                </Box>

                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  Рядків для імпорту: {data.preview.totalRows}. Перші {data.preview.sampleRows.length}:
                </Typography>
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {data.preview.headers.map((header, index) => (
                          <TableCell
                            key={index}
                            sx={{ fontWeight: isMappedColumn(index) ? 700 : 400 }}
                          >
                            {columnLabel(header, index)}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.preview.sampleRows.map((row, rowIndex) => (
                        <TableRow key={rowIndex}>
                          {data.preview?.headers.map((_header, index) => (
                            <TableCell key={index}>{row[index]}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    mt: 3,
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={actions.handleImport}
                    disabled={mapping.missingRequiredField || data.loading}
                    startIcon={data.loading ? <CircularProgress size={16} /> : undefined}
                  >
                    Імпортувати
                  </Button>
                </Box>
              </Box>
            )}

            {data.loading && !data.preview && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <CircularProgress size={24} />
              </Box>
            )}
          </Paper>
        )}
      </Box>
    </Box>
  );
}
